/**
 * Constraint layer — rewrites the "Best for" and "Tradeoff" lines when the
 * user has pinned an active constraint Priority (School / Commute / Lifestyle).
 *
 * Why this exists:
 *   Decision Lens answers "who am I?" — persona framing.
 *   Constraint Priority answers "what qualitative concern is non-negotiable
 *   today?" — situation. The same Family lens reads very differently when
 *   the active priority is School (catchment dominates) vs Commute (transit
 *   dominates). Without a constraint-aware rewrite, the lens output stays
 *   generic and users don't feel "this recommendation respects my priority".
 *
 * Where did Budget priority go?
 *   Budget used to live here as a Priority. Users read the pill + the Budget
 *   Band selector as two redundant wallet controls. Budget is now expressed
 *   through the Budget Band axis only — it composes with School (via
 *   classifyFit) and with Commute (band-aware copy below). `budgetBestFor`
 *   and `budgetTradeoff` remain in this file as existing helpers (the
 *   product brief forbids changing them) but are no longer dispatched.
 *
 * Design rules:
 *   - Pure functions. No DOM, no side effects, no network.
 *   - Priority === "none" => pass-through. The caller's fallback copy wins.
 *     This keeps the opinionated Lens interpretation the default experience.
 *   - No new schema. Uses existing tags + priceRange + verdict + reasons as
 *     proxies (e.g., "school-catchment" tag = school priority signal).
 *   - No real school rankings yet. We lean on tag proxies only.
 *   - Cascades ordered strongest signal first; generic fallback last.
 */

import type { Verdict } from "../data/verdicts";
import type { LensMode } from "./decision-lens";
import type { Priority } from "./constraint-priority";
import type { BudgetBand } from "./budget-band";
import { getSchoolFact } from "./school-facts";
import { classifyFit, fitBestFor, fitTradeoff } from "./budget-school-fit";
import { extractFacts, factPrefixCommute, type TransportFact } from "./suburb-facts";

/* -------------------------------------------------------------------------- */
/*  Small helpers — same style as single-area-lens.ts                         */
/* -------------------------------------------------------------------------- */

function tagSet(v: Verdict): Set<string> {
  return new Set((v.tags || []).map((t) => t.toLowerCase()));
}

function reasonBlob(v: Verdict): string {
  return (v.reasons || []).join(" ").toLowerCase();
}

/* -------------------------------------------------------------------------- */
/*  Fact probes — concrete hooks that must override broader tag heuristics.   */
/*                                                                            */
/*  Why these exist:                                                          */
/*    A single suburb can legitimately carry BOTH a rail tag ("transit") and  */
/*    a commute-friction tag ("long-commute" / "end-of-line" / "single-line" */
/*    / "freeway-dependent"). Before these probes, the tradeoff cascade      */
/*    treated the friction tags as equivalent to "no rail" and emitted       */
/*    "no rail option here — commute is a car or a bus slog" even for        */
/*    suburbs with a working train station (Mernda, Glen Waverley,            */
/*    Frankston, etc.). That was a trust bug.                                */
/*                                                                            */
/*    The probes return true when there IS rail access in-suburb so the      */
/*    generators can short-circuit the "no rail" branch and emit a           */
/*    rail-aware tradeoff instead. They key off the *positive* signal first  */
/*    (transit tag / rail reason text) so a no-train or bus-only tag cannot  */
/*    out-rank a concrete factual rail mention.                              */
/* -------------------------------------------------------------------------- */

/** Matches any in-suburb or adjacent rail signal present in reasons[]. */
const RAIL_REASON_RE =
  /\b(train|rail|metro|tram|line\b|station|terminus|v\s*\/?\s*line)\b/i;

/** Matches explicit rail-absence phrasing in reasons[] so an ambiguous */
/** "line" mention in a no-rail suburb is still treated as no-rail. */
const NO_RAIL_REASON_RE = /\b(no\s+train|no\s+rail|bus[-\s]only)\b/i;

/**
 * Single source of truth: delegate to the fact extractor. Keeps the public
 * `hasRailAccess` API for callers who already import it (compare-card,
 * single-area-lens, fact-coherence) but routes the actual decision through
 * the same fact pipeline that drives the copy generators. This guarantees
 * the probe and the generators can never disagree.
 */
export function hasRailAccess(v: Verdict): boolean {
  // Local import-free body — the import chain (extractFacts → tagSet) is
  // identical to what the inline implementation used.
  return extractFactsRail(v);
}

function extractFactsRail(v: Verdict): boolean {
  const t = tagSet(v);
  const reasons = reasonBlob(v);
  if (NO_RAIL_REASON_RE.test(reasons)) return false;
  if (
    t.has("transit") ||
    t.has("tram-corridor") ||
    t.has("metro-tunnel") ||
    t.has("metro")
  ) {
    return true;
  }
  return RAIL_REASON_RE.test(reasons);
}

/** True only when the suburb genuinely lacks rail (no positive tag or hook). */
export function isRailAbsent(v: Verdict): boolean {
  return !hasRailAccess(v);
}

/** Unit-agnostic "is this premium or budget" number from a priceRange string. */
function priceMidK(range: string): number | null {
  const nums = [...range.matchAll(/(\d+(?:\.\d+)?)/g)].map((m) => parseFloat(m[1]));
  if (nums.length === 0) return null;
  return (nums[0] + nums[nums.length - 1]) / 2;
}

/* -------------------------------------------------------------------------- */
/*  BUDGET priority (retained helpers, no longer dispatched)                  */
/*                                                                            */
/*  Budget was removed from the Priority options because it collided with     */
/*  the Budget Band control. The functions below are intentionally kept —     */
/*  the product brief requires that existing helpers stay unchanged. They     */
/*  are no longer called from `constrainBestFor` / `constrainTradeoff`, but   */
/*  other modules (or a future reintroduction of the pill) can still import   */
/*  the same logic without a rewrite. Marked unused so TS doesn't warn.       */
/* -------------------------------------------------------------------------- */

export function budgetBestFor(v: Verdict, mode: LensMode): string {
  const t = tagSet(v);
  const mid = priceMidK(v.priceRange);

  // Premium pocket → call it out honestly.
  if (t.has("premium-inner") || t.has("heritage") || (mid !== null && mid >= 22)) {
    if (mode === "family") return "families with headroom above the median budget";
    if (mode === "bachelor") return "renters willing to pay a premium for location";
    return "buyers where capital-growth bet outweighs entry cost";
  }

  // Value growth-corridor belt → classic cheap-entry pocket.
  if (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) {
    if (mode === "family") return "families stretching budget into a new-build suburb";
    if (mode === "bachelor") return "renters trading commute length for cheaper rent";
    return "buyers chasing a low entry price + long horizon";
  }

  // Mid-band mainstream pocket.
  if (mid !== null && mid >= 12) {
    if (mode === "family") return "families on a mainstream budget who want settled streets";
    if (mode === "bachelor") return "renters wanting a mid-budget balance of rent + access";
    return "buyers wanting a mid-band entry with stable tenant depth";
  }

  // Low-band / affordable.
  if (mode === "family") return "families shopping the lower end of the city band";
  if (mode === "bachelor") return "renters prioritising cheaper rent over nightlife";
  return "buyers prioritising low entry price over prestige";
}

export function budgetTradeoff(v: Verdict, _mode: LensMode): string {
  const t = tagSet(v);
  const mid = priceMidK(v.priceRange);

  if (t.has("premium-inner") || t.has("heritage") || (mid !== null && mid >= 22)) {
    return "entry price is well above city median — budget bites hardest here";
  }
  if (
    (t.has("growth-corridor") || t.has("new-build") || t.has("estate")) &&
    (t.has("infra-lag") || t.has("low-amenity"))
  ) {
    return "cheap entry, but amenity + transport still catching up";
  }
  if (t.has("long-commute") || t.has("no-train") || t.has("car-dependent")) {
    return "rent is cheaper, but commute + car costs eat the saving";
  }
  if (mid !== null && mid >= 12) {
    return "you're paying mid-market — not the cheapest, not the best";
  }
  return "low price reflects thinner amenity or older infra";
}

/* -------------------------------------------------------------------------- */
/*  SCHOOL priority                                                           */
/* -------------------------------------------------------------------------- */

/**
 * School Best-for / Tradeoff — grounded via the school-fact catalog.
 *
 * Before this change these functions read off raw tag proxies ("family",
 * "school-catchment") and the output landed as rewordings of the same generic
 * idea ("families anchoring around a known school catchment"). Users called it
 * vague. The fact catalog (ui/school-facts.ts) gives us a per-suburb concrete
 * hook — HPS feeder, Balwyn HS zone, McKinnon premium, "no public catchment;
 * kids commute out" — plus a `tone` field that's already calibrated to the
 * suburb's school reality ("strong" catchment / "mixed" / "emerging" /
 * "limited"). We use that tone to drive the persona framing, and embed the
 * suburb-specific fact (short `bullet` in Best-for; full `signal` in Tradeoff)
 * so the output reads grounded rather than abstract.
 *
 * Design invariants:
 *   - No new data beyond the school-facts catalog itself.
 *   - Tradeoff is literally the fact `signal` — this is the "most concrete"
 *     line and the signals are already written to carry both the fact and
 *     its tradeoff ("Balwyn HS zone — prices reflect the zoning premium").
 *   - Best-for keeps persona framing (family / bachelor / investor) but is
 *     tone-shaped by the catalog fact, with a short hook (`bullet`) appended
 *     after an em-dash so the line reads specific.
 *   - Scoring / confidence / reorder logic untouched.
 */
function schoolBestFor(v: Verdict, mode: LensMode): string {
  const fact = getSchoolFact(v);
  const hook = fact.bullet;

  if (fact.tone === "strong") {
    if (mode === "family") return `families choose this area for school access — ${hook}`;
    if (mode === "bachelor") return `renters in a school-zoned family pocket — ${hook}`;
    return `buyers chasing low-churn, zoned-family tenants — ${hook}`;
  }
  if (fact.tone === "emerging") {
    if (mode === "family") return `families banking on new-estate primaries — ${hook}`;
    if (mode === "bachelor") return "solo renters rarely — this is a family-estate pocket";
    return `buyers betting on secondary-school options forming later — ${hook}`;
  }
  if (fact.tone === "mixed") {
    if (mode === "family") return `families balancing mixed school options — ${hook}`;
    if (mode === "bachelor") return `renters — school is a side note here (${hook})`;
    return `buyers — mixed school draw; ${hook}`;
  }
  // limited — school is not the pull.
  if (mode === "family") return `families only if schooling is sorted elsewhere — ${hook}`;
  if (mode === "bachelor") return `renters — school access isn't the draw (${hook})`;
  return `buyers — school demand is thin; ${hook}`;
}

function schoolTradeoff(v: Verdict, _mode: LensMode): string {
  // The catalog `signal` is already a concrete, tone-calibrated factual
  // sentence (e.g., "Balwyn HS zone is legendary — entry prices reflect the
  // zoning premium"). Using it directly grounds the Tradeoff line in the
  // suburb's school reality instead of rewording tag proxies.
  return getSchoolFact(v).signal;
}

/* -------------------------------------------------------------------------- */
/*  COMMUTE priority                                                          */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Fact-first commute generation.                                            */
/*                                                                            */
/*  Pipeline:                                                                 */
/*    1. extractFacts(v) → structured TransportFact (hasRail, terminus,       */
/*       singleLine, busOnly, commuteQuality, commuteMinutes, freewayAdjacent)*/
/*    2. Persona interpretation chooses the framing ("families commuting by  */
/*       rail" vs "renters wanting tram/train-only").                         */
/*    3. Tradeoff is stitched as `<fact-prefix>, <friction>` — the fact is   */
/*       stated verbatim and the friction can only refine it, never replace  */
/*       it. Heuristics are allowed to pick which friction (long commute,    */
/*       terminus seats, single-line fault risk, freeway noise) but cannot   */
/*       contradict the fact.                                                */
/*                                                                            */
/*  Invariant: if `facts.transport.hasRail === true`, the emitted tradeoff   */
/*  line must mention rail positively (verified by tests in verify.mjs).     */
/* -------------------------------------------------------------------------- */

function commuteBestForFromFacts(
  f: TransportFact,
  v: Verdict,
  mode: LensMode
): string {
  const t = tagSet(v);

  // Rail access is the dominant positive fact; persona refines the framing.
  if (f.hasRail) {
    if (mode === "family") return "families with at least one parent commuting by rail";
    if (mode === "bachelor") return "renters wanting tram/train-only commute most days";
    return "buyers targeting transit-hub rental demand";
  }

  // No rail — the positive fact is short job-commute distance (IT/CBD) or a
  // genuinely car-first pocket. The persona wrappers are unchanged, but now
  // they're only reachable when the fact confirms no rail.
  if (t.has("it-corridor")) {
    if (mode === "family") return "tech families keeping school + office in one suburb";
    if (mode === "bachelor") return "young pros with a 15–25 min IT-corridor commute";
    return "buyers riding tech-corridor commute demand";
  }
  if (t.has("cbd")) {
    if (mode === "family") return "families wanting CBD-adjacent school-run + work";
    if (mode === "bachelor") return "renters with walk/short-ride CBD commute";
    return "buyers targeting CBD-commute rental depth";
  }

  if (f.busOnly || t.has("no-train") || t.has("car-dependent") || f.freewayAdjacent) {
    if (mode === "family") return "families set up for a car-first school + work routine";
    if (mode === "bachelor") return "renters who own a car + don't mind a long commute";
    return "buyers — commute is car-first; tenant pool is narrower";
  }

  if (t.has("mature-infra") || t.has("bike")) {
    if (mode === "family") return "families with a mix of tram + short-drive commute";
    if (mode === "bachelor") return "renters with a solid inner-suburb commute base";
    return "buyers — reliable commute access, mixed tenant depth";
  }

  if (mode === "family") return "families with average commute options";
  if (mode === "bachelor") return "renters — commute is fine, not a headline";
  return "buyers — commute is average for the city";
}

function commuteTradeoffFromFacts(f: TransportFact, v: Verdict): string {
  const t = tagSet(v);
  const reasons = reasonBlob(v);

  // Always open with the concrete fact. The friction clause can only refine.
  const prefix = factPrefixCommute(f);

  if (f.hasRail) {
    // Rail-bearing: friction clauses must not deny rail.
    if (f.commuteQuality === "long" && (f.terminus || t.has("end-of-line"))) {
      const mins = f.commuteMinutes !== null ? `${f.commuteMinutes}+` : "45+";
      return `${prefix}, but it's the end-of-line run — ${mins} min peak is baseline`;
    }
    if (f.commuteQuality === "long") {
      const mins = f.commuteMinutes !== null ? `${f.commuteMinutes}+` : "40+";
      return `${prefix}, but peak run is long — plan for ${mins} min`;
    }
    if (f.terminus) {
      return `${prefix} (terminus) — rail works, but seats fill fast in peak`;
    }
    if (f.singleLine) {
      return `${prefix} — it works, but a fault knocks out your only run`;
    }
    if (f.freewayAdjacent) {
      return `${prefix}, but freeway noise + drive-backup eat the margin`;
    }
    if (t.has("it-corridor") && /traffic|peak|jam/i.test(reasons)) {
      return `${prefix}, but peak traffic still stretches the drive`;
    }
    if (t.has("cbd") && /oversupply|apartment glut|tower glut/i.test(reasons)) {
      return `${prefix}; apartment oversupply drags long-run value`;
    }
    return `${prefix} — expect a small transit-corridor rent premium`;
  }

  // No rail. Now the negative framing is factually safe — the prefix has
  // already stated the absence of rail, so the friction clause doesn't
  // need to re-state it, and it cannot accidentally contradict a rail hook.
  if (f.busOnly) {
    return `${prefix} — commute is a bus-plus-train slog in peak`;
  }
  if (f.commuteQuality === "long" || t.has("car-dependent") || f.freewayAdjacent) {
    const mins = f.commuteMinutes !== null ? ` (${f.commuteMinutes}+ min peak)` : "";
    return `${prefix} — commute is a car or a long bus ride${mins}`;
  }
  if (t.has("it-corridor") && /traffic|peak|jam/i.test(reasons)) {
    return `${prefix}, short in km but peak traffic stretches the drive`;
  }
  if (t.has("cbd") && /oversupply|apartment glut|tower glut/i.test(reasons)) {
    return `${prefix}; apartment oversupply drags long-run value`;
  }
  if (t.has("arterial-noise") || t.has("industrial-adjacent")) {
    return `${prefix} — fast access, but arterial noise bleeds into weekday mornings`;
  }
  return `${prefix} — commute is workable but not a standout strength`;
}

/**
 * Thin wrappers so the rest of the module keeps its public shape. Each
 * extracts facts once, then delegates — the pipeline stays Fact → Interpret
 * → Render and is never re-ordered.
 */
function commuteBestFor(v: Verdict, mode: LensMode): string {
  return commuteBestForFromFacts(extractFacts(v).transport, v, mode);
}

function commuteTradeoff(v: Verdict, _mode: LensMode): string {
  return commuteTradeoffFromFacts(extractFacts(v).transport, v);
}

/* -------------------------------------------------------------------------- */
/*  LIFESTYLE priority                                                        */
/*                                                                            */
/*  Lifestyle sits next to School / Commute as a qualitative non-negotiable:  */
/*  cafes, nightlife, beach, village walkability. The catalog is still        */
/*  tag-proxy driven — no new data, no external APIs. Budget Band composes    */
/*  via the band-aware tradeoff branch below (Tight + Lifestyle reads        */
/*  differently from Stretch + Lifestyle).                                   */
/* -------------------------------------------------------------------------- */

function lifestyleBestFor(v: Verdict, mode: LensMode): string {
  const t = tagSet(v);

  // Strong nightlife / bar scene.
  if (t.has("nightlife") || t.has("bars")) {
    if (mode === "family") return "families who want weekend buzz at the doorstep";
    if (mode === "bachelor") return "renters living off late food + bar scene";
    return "buyers chasing lifestyle-renter demand from nightlife pockets";
  }

  // Cafe / food-scene / cultural creative.
  if (t.has("cafe-culture") || t.has("food-scene") || t.has("cultural") || t.has("creative")) {
    if (mode === "family") return "families who want cafe + weekend-market life";
    if (mode === "bachelor") return "renters living by the cafe + food scene";
    return "buyers leaning on lifestyle-renter depth";
  }

  // Beach / waterfront.
  if (t.has("beach") || t.has("waterfront")) {
    if (mode === "family") return "families trading suburbia for weekend water";
    if (mode === "bachelor") return "renters anchored to water + summer scene";
    return "buyers riding short-stay + summer-rental demand";
  }

  // Walkable village / activity centre.
  if (t.has("activity-centre") || t.has("retail-hub") || t.has("village")) {
    if (mode === "family") return "families who want a walkable village pocket";
    if (mode === "bachelor") return "renters wanting shops + cafes close by";
    return "buyers chasing walkable-village rental depth";
  }

  // Quiet / leafy — lifestyle pull is residential, not social.
  if (mode === "family") return "families for whom lifestyle means quiet weekends";
  if (mode === "bachelor") return "renters — the social scene here is thin";
  return "buyers — lifestyle pull is residential, not social";
}

function lifestyleTradeoff(v: Verdict, band: BudgetBand): string {
  const t = tagSet(v);

  // Budget × Lifestyle tilt — compact wallet-aware tradeoff. Pre-empts the
  // tag-based cases so the wallet lens reads first when the user has pinned
  // both axes. Falls through to the tag tradeoffs when band === "none".
  if (band === "tight") {
    if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene") || t.has("beach"))
      return "lifestyle pull is real, but a tight budget bites hardest in these pockets";
  }
  if (band === "stretch") {
    if (t.has("nightlife") || t.has("cafe-culture") || t.has("food-scene") || t.has("beach"))
      return "stretch budget unlocks the lifestyle postcodes — weekday noise is the cost";
  }

  if (t.has("nightlife") || t.has("bars")) {
    return "weekend buzz is a feature — weeknight noise is the cost";
  }
  if (t.has("cafe-culture") || t.has("food-scene")) {
    return "cafe + food scene is strong — rent premium reflects it";
  }
  if (t.has("beach") || t.has("waterfront")) {
    return "weekend water lifts quality of life — summer-peak demand can bite";
  }
  if (t.has("activity-centre") || t.has("retail-hub") || t.has("village")) {
    return "walkable shops + cafes — you pay a small convenience premium";
  }
  if (t.has("quiet") || t.has("leafy") || t.has("family")) {
    return "lifestyle pull is quiet residential — thin nightlife/cafe scene";
  }
  return "lifestyle offering is average — not a headline strength";
}

/* -------------------------------------------------------------------------- */
/*  Public entry points                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Rewrite the Best-for line based on the active priority. Pass-through when
 * priority === "none" so the Lens-only interpretation stays the default.
 *
 * `band` is the wallet-scale axis (Tight / Moderate / Stretch):
 *   - Under School priority it drives the Good-fit / Stretch / Weak-fit copy
 *     via `classifyFit` (the first true constraint-intersection layer).
 *   - Under Commute priority it appends a compact wallet tilt to the commute
 *     tradeoff (handled in `constrainTradeoff`) so users feel the Budget ×
 *     Commute intersection even though there's no commute-specific fit type.
 *   - Under Lifestyle it tilts the lifestyle tradeoff copy.
 *   - Under "none" the band is silent — it's an opt-in second axis.
 */
export function constrainBestFor(
  v: Verdict,
  mode: LensMode,
  priority: Priority,
  fallback: string,
  band: BudgetBand = "none"
): string {
  switch (priority) {
    case "school": {
      // Budget × School intersection — the primary two-axis output. Only
      // engages when both axes are pinned; falls back to school-only copy
      // when the user hasn't picked a budget band yet.
      const fit = classifyFit(v, band);
      if (fit) return fitBestFor(v, band, fit);
      return schoolBestFor(v, mode);
    }
    case "commute":
      return commuteBestFor(v, mode);
    case "lifestyle":
      return lifestyleBestFor(v, mode);
    case "none":
    default:
      return fallback;
  }
}

/**
 * Rewrite the Tradeoff line based on the active priority. Same pass-through
 * contract as constrainBestFor — and the same Budget × School intersection
 * upgrade when both Priority and band are set. Budget × Commute is expressed
 * here too as a short wallet tilt on the commute tradeoff.
 */
export function constrainTradeoff(
  v: Verdict,
  mode: LensMode,
  priority: Priority,
  fallback: string,
  band: BudgetBand = "none"
): string {
  switch (priority) {
    case "school": {
      const fit = classifyFit(v, band);
      if (fit) return fitTradeoff(v, band, fit);
      return schoolTradeoff(v, mode);
    }
    case "commute": {
      const base = commuteTradeoff(v, mode);
      // Budget × Commute intersection — no new fit classifier, just a
      // band-aware tilt so the wallet axis composes with the commute axis.
      // Silent when band === "none" to keep existing copy untouched.
      if (band === "tight") return `${base} — on a tight budget, transit-premium pockets will pinch`;
      if (band === "moderate") return `${base} — a mid-range budget fits the mid-commute belt cleanly`;
      if (band === "stretch") return `${base} — a stretch budget opens the transit-premium pockets`;
      return base;
    }
    case "lifestyle":
      return lifestyleTradeoff(v, band);
    case "none":
    default:
      return fallback;
  }
}

/* -------------------------------------------------------------------------- */
/*  Reason reordering — emphasise reasons relevant to the active priority     */
/* -------------------------------------------------------------------------- */

/**
 * Reorder the 3 reasons so whichever one best matches the priority floats to
 * the top. Non-destructive — returns the same three strings, just a reordered
 * tuple, so downstream UI code (which expects 3) is untouched.
 *
 * When priority === "none" this is a no-op.
 */
export function reorderReasonsByPriority(
  reasons: readonly [string, string, string],
  priority: Priority
): [string, string, string] {
  if (priority === "none") return [reasons[0], reasons[1], reasons[2]];

  const kw =
    priority === "school"
      ? /school|catchment|education|kids|primary|secondary|college/i
      : priority === "commute"
      ? /commute|transit|tram|train|metro|bus|rail|drive|traffic|minute|min\b|km/i
      : /cafe|bar|restaurant|nightlife|food|scene|beach|waterfront|village|walkable|market|cultural|creative/i;

  const ranked = [...reasons]
    .map((text, idx) => ({ text, idx, hit: kw.test(text) ? 1 : 0 }))
    .sort((a, b) => (b.hit - a.hit) || (a.idx - b.idx));

  return [ranked[0].text, ranked[1].text, ranked[2].text];
}
